import type { EditPatch, EditorRole } from './types.ts';

export type ResolvedTarget = {
	node: HTMLElement;
	attribute?: string; // e.g. 'src', 'href'; undefined means use style/innerText
};

const BG_IMAGE_URL_ATTRIBUTE = '__bw_bg_image_url__';
const URL_TOKEN_REGEX = /url\(\s*(['"]?)(.*?)\1\s*\)/i;

/**
 * Resolve the DOM node and attribute to read/write for a given role + property.
 * Returns null if the target cannot be resolved (e.g., no anchor found for href).
 */
export function resolveTarget(
	element: HTMLElement,
	role: EditorRole,
	property: keyof EditPatch,
): ResolvedTarget | null {
	switch (property) {
		case 'textContent': {
			if (role === 'heading' || role === 'text') {
				return { node: element };
			}
			if (role === 'btn') {
				return { node: resolveButtonLabelNode(element) };
			}
			return null;
		}

		case 'backgroundColor': {
			if (role === 'body' || role === 'section') {
				return { node: element };
			}
			if (role === 'btn') {
				return { node: resolveButtonFillNode(element) };
			}
			return null;
		}

		case 'textColor': {
			if (role === 'heading' || role === 'text') {
				return { node: element };
			}
			if (role === 'btn') {
				const anchor = resolveButtonAnchorNode(element) ?? element;
				return { node: anchor };
			}
			return null;
		}

		case 'borderColor': {
			if (role === 'btn') {
				return { node: resolveButtonFillNode(element) };
			}
			return null;
		}

		case 'src': {
			if (role !== 'img') return null;
			return resolveImageSourceTarget(element);
		}

		case 'href': {
			if (role !== 'img' && role !== 'btn') return null;
			const anchor =
				role === 'img'
					? resolveImageAnchorNode(element)
					: resolveButtonAnchorNode(element);
			return anchor ? { node: anchor, attribute: 'href' } : null;
		}

		default:
			return null;
	}
}

function resolveImageNode(element: HTMLElement): HTMLImageElement | null {
	if (element.tagName === 'IMG') return element as HTMLImageElement;
	return element.querySelector<HTMLImageElement>('img');
}

function resolveImageSourceTarget(element: HTMLElement): ResolvedTarget | null {
	const imageNode = resolveImageNode(element);
	if (imageNode) return { node: imageNode, attribute: 'src' };

	if (readBackgroundImageUrl(element)) {
		return { node: element, attribute: BG_IMAGE_URL_ATTRIBUTE };
	}

	return null;
}

function resolveImageAnchorNode(element: HTMLElement): HTMLElement | null {
	if (element.tagName === 'A') return element;
	const ancestor = element.closest('a');
	if (ancestor) return ancestor as HTMLElement;
	return null;
}

function resolveButtonAnchorNode(element: HTMLElement): HTMLElement | null {
	if (element.tagName === 'A') return element;
	const ancestor = element.closest('a');
	if (ancestor) return ancestor as HTMLElement;
	return element.querySelector('a');
}

function resolveButtonLabelNode(element: HTMLElement): HTMLElement {
	const anchor = resolveButtonAnchorNode(element) ?? element;
	const spans = Array.from(anchor.querySelectorAll<HTMLElement>('span'));
	for (let i = spans.length - 1; i >= 0; i -= 1) {
		const span = spans[i];
		if (span.children.length === 0 && span.textContent?.trim()) {
			return span;
		}
	}
	return anchor;
}

function resolveButtonFillNode(element: HTMLElement): HTMLElement {
	const anchor = resolveButtonAnchorNode(element) ?? element;
	const classHint = anchor.querySelector<HTMLElement>('.button');
	if (classHint) return classHint;

	const candidates = [
		anchor,
		...Array.from(anchor.querySelectorAll<HTMLElement>('span,div,td')),
	];
	return (
		candidates.find((candidate) => {
			return Boolean(
				candidate.style.backgroundColor || candidate.getAttribute('bgcolor'),
			);
		}) ?? anchor
	);
}

function extractStyleDeclarationValue(
	styleValue: string,
	property: string,
): string | null {
	const regex = new RegExp(`${property}\\s*:\\s*([^;]+)`, 'i');
	const match = styleValue.match(regex);
	if (!match) return null;
	return match[1].trim();
}

function replaceStyleDeclarationValue(
	styleValue: string,
	property: string,
	nextValue: string,
): string | null {
	const regex = new RegExp(`(${property}\\s*:\\s*)([^;]+)`, 'i');
	if (!regex.test(styleValue)) return null;
	return styleValue.replace(regex, `$1${nextValue}`);
}

function extractFirstUrlToken(input: string): string | null {
	const match = input.match(URL_TOKEN_REGEX);
	if (!match) return null;
	return match[2].trim();
}

function replaceFirstUrlToken(input: string, nextUrl: string): string | null {
	const match = input.match(URL_TOKEN_REGEX);
	if (!match) return null;
	const quote = match[1] ?? '';
	return input.replace(URL_TOKEN_REGEX, `url(${quote}${nextUrl}${quote})`);
}

function readBackgroundImageUrl(element: HTMLElement): string | null {
	const styleAttr = element.getAttribute('style');
	if (styleAttr) {
		const bgImageDeclaration = extractStyleDeclarationValue(
			styleAttr,
			'background-image',
		);
		if (bgImageDeclaration) {
			const fromInlineStyle = extractFirstUrlToken(bgImageDeclaration);
			if (fromInlineStyle) return fromInlineStyle;
		}
	}

	if (element.style.backgroundImage) {
		return extractFirstUrlToken(element.style.backgroundImage);
	}

	return null;
}

function writeBackgroundImageUrl(
	element: HTMLElement,
	nextUrl: string,
): boolean {
	const styleAttr = element.getAttribute('style');
	if (styleAttr) {
		const bgImageDeclaration = extractStyleDeclarationValue(
			styleAttr,
			'background-image',
		);
		if (bgImageDeclaration) {
			const updatedBgImage = replaceFirstUrlToken(bgImageDeclaration, nextUrl);
			if (!updatedBgImage) return false;
			const updatedStyle = replaceStyleDeclarationValue(
				styleAttr,
				'background-image',
				updatedBgImage,
			);
			if (!updatedStyle) return false;
			element.setAttribute('style', updatedStyle);
			return true;
		}
	}

	const currentBgImage = element.style.backgroundImage;
	const updatedBgImage = replaceFirstUrlToken(currentBgImage, nextUrl);
	if (!updatedBgImage) return false;
	element.style.backgroundImage = updatedBgImage;
	return true;
}

/** Read the element's inline text color. */
function getNodeTextColor(node: HTMLElement): string {
	return node.style.color || '';
}

/** Read the element's inline border color, trying multiple representations. */
function getNodeBorderColor(node: HTMLElement): string {
	if (node.style.borderColor) return node.style.borderColor;
	// Fall back to parsing the border shorthand for a color token
	const border = node.style.border;
	if (border) {
		const m = border.match(/(#[0-9a-fA-F]{3,8}|rgba?\([^)]+\))/);
		if (m) return m[1];
	}
	// Fall back to the raw style attribute (handles border-color: set as a raw string)
	const styleAttr = node.getAttribute('style');
	if (styleAttr) {
		const v = extractStyleDeclarationValue(styleAttr, 'border-color');
		if (v) return v;
	}
	return '';
}

/** Apply a border color to a node, preserving existing border shorthand structure. */
function applyBorderColorToNode(node: HTMLElement, value: string): void {
	const COLOR_RE = /(#[0-9a-fA-F]{3,8}|rgba?\([^)]+\))/;
	const styleAttr = node.getAttribute('style');
	if (styleAttr) {
		// Update the color inside a border: shorthand if it contains one
		const borderDecl = extractStyleDeclarationValue(styleAttr, 'border');
		if (borderDecl && COLOR_RE.test(borderDecl)) {
			const updated = replaceStyleDeclarationValue(
				styleAttr,
				'border',
				borderDecl.replace(COLOR_RE, value),
			);
			if (updated) {
				node.setAttribute('style', updated);
			}
		}
		// Update border-color: if it exists as its own declaration
		const bcDecl = extractStyleDeclarationValue(styleAttr, 'border-color');
		if (bcDecl) {
			const updated = replaceStyleDeclarationValue(
				styleAttr,
				'border-color',
				value,
			);
			if (updated) {
				node.setAttribute('style', updated);
			}
		}
	}
	// Always set via CSSOM as a catch-all (handles cases not covered above)
	node.style.borderColor = value;
}

const TRANSPARENT = 'rgba(0, 0, 0, 0)';
const PAGE_FRAME_WIDTH_RATIO = 0.9;
const MAX_PAGE_FRAME_DEPTH = 50;

/** The element's own explicitly-set background (inline style or legacy bgcolor attr). */
function getNodeBackgroundColor(node: HTMLElement): string {
	return node.style.backgroundColor || node.getAttribute('bgcolor') || '';
}

/** The element's rendered background color, normalized; '' when transparent. */
function getComputedBackgroundColor(node: HTMLElement): string {
	const view = node.ownerDocument?.defaultView;
	if (!view) return getNodeBackgroundColor(node);
	const bg = view.getComputedStyle(node).backgroundColor;
	if (!bg || bg === TRANSPARENT || bg === 'transparent') return '';
	return bg;
}

function isHidden(node: HTMLElement): boolean {
	const view = node.ownerDocument?.defaultView;
	if (!view) return false;
	const style = view.getComputedStyle(node);
	return style.display === 'none' || style.visibility === 'hidden';
}

/** Visible element children (skips text/comment nodes and hidden elements like preheaders). */
function visibleElementChildren(node: HTMLElement): HTMLElement[] {
	return (Array.from(node.children) as HTMLElement[]).filter(
		(child) => !isHidden(child),
	);
}

/** Apply a background color to a single node, mirroring its existing background style. */
function applyBackgroundColorToNode(node: HTMLElement, value: string): void {
	node.style.backgroundColor = value;
	if (node.hasAttribute('bgcolor')) {
		node.setAttribute('bgcolor', value);
	}
	const shorthandBackground = node.style.background;
	if (
		shorthandBackground &&
		!shorthandBackground.toLowerCase().includes('gradient')
	) {
		node.style.background = value;
	}
}

/**
 * Collect the "page frame": the body plus the chain of full-width wrapper layers
 * above the centered content column. These are the layers that actually paint the
 * page background the user sees (the body's own background is often hidden behind them).
 *
 * Walk straight down from the body and stop at the first layer that:
 *   (a) renders visibly narrower than the body (the centered content column), or
 *   (b) branches into more than one visible child (content sections begin), or
 *   (c) has a background color different from the established page color (a content
 *       block layered on top of the page background).
 */
function collectPageFrameNodes(body: HTMLElement): HTMLElement[] {
	const frame: HTMLElement[] = [body];
	const bodyWidth = body.getBoundingClientRect().width;
	// The body's own color does NOT define the page color (it is often a hidden
	// fallback layer); the first wrapper below it that has a background establishes it.
	let pageColor = '';
	let current: HTMLElement = body;

	for (let depth = 0; depth < MAX_PAGE_FRAME_DEPTH; depth += 1) {
		const children = visibleElementChildren(current);
		if (children.length !== 1) break; // (b) branch or leaf reached
		const next = children[0];

		// (a) the centered content column has begun
		const nextWidth = next.getBoundingClientRect().width;
		if (bodyWidth > 0 && nextWidth < bodyWidth * PAGE_FRAME_WIDTH_RATIO) break;

		const nextColor = getComputedBackgroundColor(next);
		if (nextColor) {
			if (!pageColor) {
				pageColor = nextColor;
			} else if (nextColor !== pageColor) {
				break; // (c) a differently-colored content block sits on top
			}
		}

		frame.push(next);
		current = next;
	}

	return frame;
}

/**
 * Read the current value of a property from the DOM.
 */
export function readPropertyValue(
	element: HTMLElement,
	role: EditorRole,
	property: keyof EditPatch,
): string | null {
	const target = resolveTarget(element, role, property);
	if (!target) return null;

	switch (property) {
		case 'textContent':
			return target.node.innerText;

		case 'backgroundColor': {
			if (role === 'body') {
				// Read the visible page color from the innermost frame layer that has one,
				// not the body's own (often-hidden) background.
				const frame = collectPageFrameNodes(target.node);
				for (let i = frame.length - 1; i >= 0; i -= 1) {
					const color = getNodeBackgroundColor(frame[i]);
					if (color) return color;
				}
				return '';
			}
			return getNodeBackgroundColor(target.node);
		}

		case 'src':
			if (target.attribute === BG_IMAGE_URL_ATTRIBUTE) {
				return readBackgroundImageUrl(target.node) || '';
			}
			return target.node.getAttribute(target.attribute!) || '';

		case 'href':
			return target.node.getAttribute(target.attribute!) || '';

		case 'textColor':
			return getNodeTextColor(target.node);

		case 'borderColor':
			return getNodeBorderColor(target.node);

		default:
			return null;
	}
}

/**
 * Write a property value to the DOM.
 */
export function writePropertyValue(
	element: HTMLElement,
	role: EditorRole,
	property: keyof EditPatch,
	value: string,
): boolean {
	const target = resolveTarget(element, role, property);
	if (!target) return false;

	switch (property) {
		case 'textContent':
			target.node.textContent = value;
			return true;

		case 'backgroundColor': {
			if (role === 'body') {
				// Recolor the body (fallback layer) plus every full-width wrapper layer
				// that paints the page background, so the change shows regardless of
				// which layer a given email client renders.
				const frame = collectPageFrameNodes(target.node);
				applyBackgroundColorToNode(target.node, value);
				for (const node of frame) {
					if (node !== target.node && getNodeBackgroundColor(node)) {
						applyBackgroundColorToNode(node, value);
					}
				}
				return true;
			}
			applyBackgroundColorToNode(target.node, value);
			return true;
		}

		case 'src':
			if (target.attribute === BG_IMAGE_URL_ATTRIBUTE) {
				return writeBackgroundImageUrl(target.node, value);
			}
			target.node.setAttribute(target.attribute!, value);
			return true;

		case 'href':
			target.node.setAttribute(target.attribute!, value);
			return true;

		case 'textColor':
			target.node.style.color = value;
			if (role === 'btn') {
				for (const el of Array.from(
					target.node.querySelectorAll<HTMLElement>('*'),
				)) {
					if (el.style.color) el.style.color = value;
				}
			}
			return true;

		case 'borderColor':
			applyBorderColorToNode(target.node, value);
			return true;

		default:
			return false;
	}
}
